// frontend/src/components/qa/QABugDrawer.tsx
// Slide-in drawer that wraps QABugForm and submits to /api/qa/bugs.
// Falls back to localStorage queue on network failure.

import { useEffect, useState } from 'react'
import api from '../../utils/api'
import QABugForm from './QABugForm'
import type { QAContext } from './QABugForm'
import { useLanguage } from '../../i18n/index.jsx'

const PENDING_KEY = 'stagewise:qa:pending'

type BugPayload = Record<string, unknown>

interface PendingEntry {
  payload: BugPayload
  queuedAt: number
}

interface SubmitResult {
  ok: boolean
  message: string
}

interface QABugDrawerProps {
  context: QAContext
  onClose: () => void
  onSubmitted: (result: SubmitResult) => void
}

function queueLocally(payload: BugPayload) {
  try {
    const existing: PendingEntry[] = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]')
    existing.push({ payload, queuedAt: Date.now() })
    const trimmed = existing.slice(-50)
    localStorage.setItem(PENDING_KEY, JSON.stringify(trimmed))
  } catch { /* localStorage full — silently drop */ }
}

async function flushPending() {
  try {
    const queue: PendingEntry[] = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]')
    if (!queue.length) return
    const successful: PendingEntry[] = []
    for (const entry of queue) {
      try {
        await api.post('/qa/bugs', entry.payload)
        successful.push(entry)
      } catch { break }
    }
    if (successful.length) {
      const remaining = queue.filter(e => !successful.includes(e))
      localStorage.setItem(PENDING_KEY, JSON.stringify(remaining))
    }
  } catch { /* ignore malformed queue */ }
}

export default function QABugDrawer({ context, onClose, onSubmitted }: QABugDrawerProps) {
  const { t } = useLanguage()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleEsc)
    flushPending()
    return () => document.removeEventListener('keydown', handleEsc)
  }, [onClose])

  async function handleSubmit(payload: BugPayload) {
    setSubmitting(true)
    try {
      await api.post('/qa/bugs', payload)
      onSubmitted({ ok: true, message: t('qaToastReported') })
      onClose()
    } catch (err) {
      const e = err as { response?: { status?: number } }
      if (e?.response?.status === 403) {
        onSubmitted({ ok: false, message: t('qaToastDisabled') })
        onClose()
        return
      }
      queueLocally(payload)
      onSubmitted({ ok: false, message: t('qaToastSavedOffline') })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="qa-drawer" role="dialog" aria-label={t('qaDrawerTitle')}>
      <div className="qa-drawer-header">
        <h3>{t('qaDrawerTitle')}</h3>
        <button onClick={onClose} aria-label={t('qaDrawerClose')}>×</button>
      </div>
      <div className="qa-drawer-body">
        <QABugForm
          context={context}
          onSubmit={handleSubmit}
          onCancel={onClose}
          submitting={submitting}
        />
      </div>
    </div>
  )
}
